import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import MenteeDashboardHeader from "@/components/mentee/MenteeDashboardHeader";
import MenteeDashboardActions from "@/components/mentee/MenteeDashboardActions";
import RecommendedMentorsList, { MentorData } from "@/components/mentee/RecommendedMentorsList";
import ConnectedMentorsList from "@/components/mentee/ConnectedMentorsList";
import MentorApplications from "@/components/mentee/MentorApplications";
import UpcomingAppointments from "@/components/mentee/UpcomingAppointments";
import CompletedAppointments from "@/components/mentee/CompletedAppointments";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import ActiveSubscriptionsSection from "@/components/ActiveSubscriptionsSection";
import SubscriptionSuccessDialog from "@/components/SubscriptionSuccessDialog";

const MenteeDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [userName, setUserName] = useState("User");
  const [recommendedMentors, setRecommendedMentors] = useState<MentorData[]>([]);
  const [isLoadingMentors, setIsLoadingMentors] = useState(true);
  const [avatarUrl, setAvatarUrl] = useState("");
  const [connectedMentors, setConnectedMentors] = useState<MentorData[]>([]);
  const [isLoadingConnections, setIsLoadingConnections] = useState(true);
  const [userId, setUserId] = useState<string>("");
  const [hasPendingReviews, setHasPendingReviews] = useState(false);

  useEffect(() => {
    document.title = "Mentee Dashboard | EMU Mentoring";
  }, []);

  // New state for subscription success modal
  const [showSubscriptionSuccess, setShowSubscriptionSuccess] = useState(false);
  const [subscriptionMentor, setSubscriptionMentor] = useState<{ id: string; name: string } | null>(null);

  // Check for subscription success parameter
  useEffect(() => {
    const subscriptionActivated = searchParams.get('subscriptionActivated');
    const mentorId = searchParams.get('mentorId');
    
    if (subscriptionActivated === 'true') {
      // Remove the parameters from URL to prevent re-triggering
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('subscriptionActivated');
      newSearchParams.delete('mentorId');
      setSearchParams(newSearchParams, { replace: true });
      
      // Show the success modal
      setShowSubscriptionSuccess(true);
      
      // If mentor ID is provided, fetch mentor information
      if (mentorId && userId) {
        fetchMentorInfo(mentorId);
      }
    }
  }, [searchParams, setSearchParams, userId]);

  const fetchMentorInfo = async (mentorId: string) => {
    try {
      const { data: mentorData, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .eq('id', mentorId)
        .single();
        
      if (!error && mentorData) {
        setSubscriptionMentor({
          id: mentorData.id,
          name: `${mentorData.first_name || ''} ${mentorData.last_name || ''}`.trim() || 'Your Mentor'
        });
      }
    } catch (error) {
      console.error('Error fetching mentor info:', error);
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        console.log("Fetching user data for mentee dashboard");
        const userData = localStorage.getItem("userData");
        if (userData) {
          const parsed = JSON.parse(userData);
          if (parsed.firstName) {
            setUserName(parsed.firstName);
          }
          
          if (parsed.profileType !== "mentee" && parsed.profileType !== "both") {
            navigate("/dashboard");
            return;
          }

          if (parsed.id) {
            setUserId(parsed.id);
            
            const { data, error } = await supabase
              .from('profiles')
              .select('avatar_url')
              .eq('id', parsed.id)
              .single();

            if (!error && data?.avatar_url) {
              setAvatarUrl(data.avatar_url);
            }
          }
        } else {
          navigate("/login");
        }
      } catch (e) {
        console.error("Error in fetchUserData:", e);
      }
    };

    fetchUserData();
  }, [navigate]);

  useEffect(() => {
    async function fetchRecommendedMentors() {
      setIsLoadingMentors(true);
      
      try {
        console.log("Fetching recommended mentors...");
        
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('profile_type', 'mentor')
          .eq('status', 'Active')
          .limit(3);
        
        if (error) {
          console.error("Error fetching recommended mentors:", error);
          toast({
            title: "Error",
            description: "Failed to load recommended mentors.",
            variant: "destructive",
          });
          setIsLoadingMentors(false);
          return;
        }
        
        if (!data || data.length === 0) {
          setRecommendedMentors([]);
          setIsLoadingMentors(false);
          return;
        }
        
        const transformedMentors: MentorData[] = data.map(mentor => {
          let specialtiesArray: string[] = ['Mentoring'];
          
          if (mentor.specialties) {
            try {
              if (typeof mentor.specialties === 'string') {
                const parsed = JSON.parse(mentor.specialties);
                if (Array.isArray(parsed)) {
                  specialtiesArray = parsed;
                } else if (typeof parsed === 'string') {
                  specialtiesArray = [parsed];
                }
              } else if (Array.isArray(mentor.specialties)) {
                specialtiesArray = mentor.specialties;
              }
            } catch (e) {
              console.error("Error parsing specialties:", e);
            }
          }
          
          const fullName = `${mentor.first_name || ''} ${mentor.last_name || ''}`.trim() || 'Anonymous Mentor';
          
          return {
            id: mentor.id,
            name: fullName,
            profession: mentor.profession || 'Professional Mentor',
            location: mentor.location || 'Remote',
            avatar: mentor.avatar_url || '',
            specialties: specialtiesArray
          };
        });
        
        setRecommendedMentors(transformedMentors);
      } catch (error) {
        console.error("Exception in fetchRecommendedMentors:", error);
        toast({
          title: "Error",
          description: "An unexpected error occurred.",
          variant: "destructive",
        });
      } finally {
        setIsLoadingMentors(false);
      }
    }
    
    fetchRecommendedMentors();
  }, [toast]);

  useEffect(() => {
    async function fetchConnectedMentors() {
      setIsLoadingConnections(true);
      
      try {
        if (!userId) {
          setConnectedMentors([]);
          setIsLoadingConnections(false);
          return;
        }
        
        console.log("Fetching connected mentors for user:", userId);
        
        // Get applications that are either 'paid' or 'completed'
        const { data: applicationData, error: applicationError } = await supabase
          .from('applications')
          .select('mentor_id')
          .eq('user_id', userId)
          .in('status', ['paid', 'completed']);
        
        if (applicationError) {
          console.error("Error fetching applications:", applicationError);
          toast({
            title: "Error",
            description: "Failed to load your mentor connections.",
            variant: "destructive",
          });
          setIsLoadingConnections(false);
          return;
        }
        
        console.log("Application data:", applicationData);
        
        if (!applicationData || applicationData.length === 0) {
          console.log("No connected mentors found");
          setConnectedMentors([]);
          setIsLoadingConnections(false);
          return;
        }
        
        // Extract unique mentor IDs
        const mentorIds = [...new Set(applicationData.map(app => app.mentor_id))];
        
        // Fetch mentor profiles
        const { data: mentorProfiles, error: mentorError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, avatar_url, profession, location, specialties')
          .in('id', mentorIds);
        
        if (mentorError) {
          console.error("Error fetching mentor profiles:", mentorError);
          toast({
            title: "Error",
            description: "Failed to load mentor profiles.",
            variant: "destructive",
          });
          setIsLoadingConnections(false);
          return;
        }
        
        // Transform data to match MentorData format
        const transformedMentors: MentorData[] = mentorProfiles.map(mentor => {
          // Parse specialties if they exist and are in JSON format
          let specialtiesArray: string[] = [];
          if (mentor.specialties) {
            try {
              if (typeof mentor.specialties === 'string') {
                const parsed = JSON.parse(mentor.specialties);
                if (Array.isArray(parsed)) {
                  specialtiesArray = parsed;
                } else if (typeof parsed === 'string') {
                  specialtiesArray = [parsed];
                }
              } else if (Array.isArray(mentor.specialties)) {
                specialtiesArray = mentor.specialties;
              }
            } catch (e) {
              console.error("Error parsing specialties:", e);
              specialtiesArray = ['Mentoring'];
            }
          }
          
          return {
            id: mentor.id,
            name: `${mentor.first_name || ''} ${mentor.last_name || ''}`.trim() || 'Anonymous Mentor',
            profession: mentor.profession || 'Professional Mentor',
            location: mentor.location || 'Remote',
            avatar: mentor.avatar_url || '',
            specialties: specialtiesArray,
            rating: 5, // Default rating
            reviews: 0 // Default review count
          };
        });
        
        console.log("Transformed connected mentors:", transformedMentors);
        setConnectedMentors(transformedMentors);
      } catch (error) {
        console.error("Exception in fetchConnectedMentors:", error);
        toast({
          title: "Error",
          description: "Failed to load your mentor connections.",
          variant: "destructive",
        });
      } finally {
        setIsLoadingConnections(false);
      }
    }
    
    fetchConnectedMentors();
  }, [userId, toast]);

  const handleNavigation = (route: string) => {
    navigate(route);
  };

  const handleViewProfile = (mentorId: string) => {
    navigate(`/mentor-profile/${mentorId}`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      
      <main className="flex-grow pt-0 pb-12">
        <MenteeDashboardHeader
          userName={userName}
          avatarUrl={avatarUrl}
          onNavigate={handleNavigation}
        />
        
        <div className="container mx-auto px-4 sm:px-6">
          {/* Welcome Card and Profile Completion */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mt-6 sm:mt-8">
            <Card className="lg:col-span-2 bg-white rounded-lg shadow-sm border p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-center sm:items-start">
                <div className="w-20 h-20 sm:w-24 sm:h-24 relative">
                  <Avatar className="w-20 h-20 sm:w-24 sm:h-24 border-4 border-white shadow-md">
                    <AvatarImage src={avatarUrl} alt={userName} />
                    <AvatarFallback className="text-2xl sm:text-4xl font-medium">
                      {userName.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">
                    Welcome, {userName}
                  </h2>
                  <p className="text-gray-500 mb-2 text-sm sm:text-base">
                    to your Mentee Dashboard
                  </p>
                </div>
              </div>
            </Card>
            
            <Card className="bg-white rounded-lg shadow-sm border p-4 sm:p-6">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold text-gray-800 text-xs sm:text-sm">Account Settings</h3>
                <ArrowRight size={16} className="text-gray-400 flex-shrink-0" />
              </div>
              <div className="text-xs sm:text-sm text-gray-500 mb-4">Manage your password and payment information</div>
              <Button 
                className="w-full bg-emu-blue hover:bg-emu-blue/90 text-sm sm:text-base h-10 sm:h-11"
                onClick={() => handleNavigation(`/account`)}
              >
                <span className="hidden sm:inline">Account Settings</span>
                <span className="sm:hidden">Account Settings</span>
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </Card>
          </div>

          <div className="max-w-7xl mx-auto mt-8">
            <MenteeDashboardActions 
              onNavigate={handleNavigation}
            />
            
            {/* Active Subscriptions Section */}
            <div className="mt-12">
              <ActiveSubscriptionsSection 
                userId={userId} 
                userType="mentee" 
              />
            </div>
            
            {/* Dashboard Sections with Consistent Spacing */}
            <div className="mt-12">
              <h2 className="text-xl font-semibold text-gray-800 mb-6">Appointments</h2>
              <UpcomingAppointments />
            </div>
            
            <div className="mt-12">
              <h2 className="text-xl font-semibold text-gray-800 mb-6">Leave Review</h2>
              <CompletedAppointments onPendingReviewsChange={setHasPendingReviews} />
            </div>
            
            <div className="mt-12">
              <MentorApplications />
            </div>
            
            <div className="mt-12">
              <ConnectedMentorsList
                isLoading={isLoadingConnections}
                connectedMentors={connectedMentors}
                onViewProfile={handleViewProfile}
                onNavigate={handleNavigation}
              />
            </div>
            
            <div className="mt-12">
              <RecommendedMentorsList
                isLoading={isLoadingMentors}
                mentors={recommendedMentors}
                onViewProfile={handleViewProfile}
                onNavigate={handleNavigation}
              />
            </div>
          </div>
        </div>
      </main>
      
      {/* Subscription Success Modal */}
      <SubscriptionSuccessDialog
        isOpen={showSubscriptionSuccess}
        onClose={() => setShowSubscriptionSuccess(false)}
        mentorId={subscriptionMentor?.id}
        mentorName={subscriptionMentor?.name}
      />
      
      <Footer />
    </div>
  );
};

export default MenteeDashboard;
